import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type FormFilterState = {
  searchTerm: string;
  status: string[];
};

type FormFiltersProps = {
  filters: FormFilterState;
  onFiltersChange: (filters: FormFilterState) => void;
  statuses: string[];
  counts?: {
    status: Record<string, number>;
  };
};

type TabType = "resumo" | "status";

export function FormFilters({
  filters,
  onFiltersChange,
  statuses,
  counts,
}: FormFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");

  const filterCount = filters.status.length;

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      status: [],
    });
  };

  const removeFilter = (type: keyof FormFilterState, value?: string) => {
    if (type === "status") {
      onFiltersChange({
        ...filters,
        status: value ? filters.status.filter((v) => v !== value) : [],
      });
    }
  };

  const tabs: Array<{ id: TabType; label: string; icon: string }> = [
    { id: "resumo", label: "Resumo", icon: "📋" },
    { id: "status", label: "Status", icon: "📊" },
  ];

  const getTabCount = (tabId: TabType) => {
    switch (tabId) {
      case "status": return filters.status.length;
      default: return 0;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="relative">
          <Filter className="h-4 w-4 mr-2" />
          Filtros
          {filterCount > 0 && (
            <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
              {filterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="flex h-[350px]">
          {/* Sidebar */}
          <div className="w-48 border-r bg-muted/30 p-2 overflow-y-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </span>
                  {count > 0 && (
                    <Badge variant={activeTab === tab.id ? "secondary" : "default"} className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>

                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.status.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.status.map((s) => (
                            <Badge key={s} variant="secondary" className="gap-1 capitalize">
                              {s}
                              <X
                                className="h-3 w-3 cursor-pointer"
                                onClick={() => removeFilter("status", s)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={filters.status.includes(status)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            status: checked
                              ? [...filters.status, status]
                              : filters.status.filter((s) => s !== status),
                          });
                        }}
                      />
                      <Label htmlFor={`status-${status}`} className="capitalize flex-1 flex items-center gap-2">
                        {status}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">
                          {counts?.status?.[status] ?? 0}
                        </Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

